import Breadcrumb from "@/components/feature/Breadcrumb";

const principles = [
  { icon: "ri-time-line", title: "实时更新", desc: "第一时间同步连载作品的最新话与更新时间，追更不迟到。" },
  { icon: "ri-filter-line", title: "分类清晰", desc: "连载中与已完结分开排列，按题材分类，快速定位你想追的作品。" },
  { icon: "ri-error-warning-line", title: "信息严谨", desc: "话数、状态、更新时间力求准确，发现错误及时核实修正。" },
  { icon: "ri-user-smile-line", title: "读者为本", desc: "一切以读者的追更体验为出发点，让追更更轻松。" },
];

const testimonials = [
  {
    name: "小陈同学",
    role: "追更老粉",
    text: "每天打开无尽漫画网看一眼今日更新，我追的几部到第几话一目了然，再也不会错过更新了。",
  },
  {
    name: "阿默",
    role: "完结党",
    text: "已完结的作品分类得很清楚，想看长篇就找「已完结」，一口气追到底的感觉太爽了。",
  },
  {
    name: "柚子",
    role: "漫迷",
    text: "分类和状态筛选做得细，找同题材的连载很方便，追更进度随时能查。",
  },
];

export default function About() {
  return (
    <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-12">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            name: "关于无尽漫画网",
            description: "无尽漫画网的品牌介绍、定位、理念、内容原则与联系方式。",
            mainEntity: {
              "@type": "Organization",
              name: "无尽漫画网",
              email: "support@wujinmanhua.com",
            },
          }),
        }}
      />

      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <div className="mt-6 max-w-3xl">
        <h1 className="text-2xl font-bold text-foreground-950 md:text-3xl">关于无尽漫画网</h1>
        <p className="mt-3 prose-line text-sm text-foreground-700">
          无尽漫画网是一个专注漫画追更情报的内容门户。我们围绕「今日更新」「最新话」「更新到第几话」「追更进度」这些最核心的诉求，为漫画读者提供一目了然的追更服务。
        </p>
      </div>

      {/* 定位 */}
      <section className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="rounded-lg bg-primary-50 p-6 md:p-8">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-primary-500 text-background-50">
            <i className="ri-crosshair-2-line text-xl"></i>
          </span>
          <h2 className="mt-4 text-xl font-semibold text-foreground-950">网站定位</h2>
          <p className="mt-3 prose-line text-sm text-foreground-700">
            我们是漫画读者的「追更助手」——不生产漫画，专注整理漫画的更新状态与进度。连载中与已完结分开排列，按状态筛选即可快速定位每一部作品的最新话。
          </p>
        </div>
        <div className="rounded-lg bg-accent-50 p-6 md:p-8">
          <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-accent-500 text-background-50">
            <i className="ri-compass-3-line text-xl"></i>
          </span>
          <h2 className="mt-4 text-xl font-semibold text-foreground-950">发展理念</h2>
          <p className="mt-3 prose-line text-sm text-foreground-700">
            我们相信，追更是热爱，更是一种陪伴。无尽漫画网希望成为每一位漫迷的可靠伙伴，让你把追更的精力留给剧情，而不是四处打听「更到第几话」。
          </p>
        </div>
      </section>

      {/* 内容原则 */}
      <section className="mt-12">
        <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">我们的内容原则</h2>
        <p className="mt-1 text-sm text-foreground-600">四个坚持，成就可信赖的追更情报</p>
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {principles.map((p) => (
            <div key={p.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="flex h-10 w-10 items-center justify-center rounded-md bg-background-100 text-primary-500">
                <i className={`${p.icon} text-lg`}></i>
              </span>
              <h3 className="mt-3 text-sm font-semibold text-foreground-950">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* 用户口碑 */}
      <section className="mt-12 rounded-lg bg-background-100/70 p-6 md:p-10">
        <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">读者怎么说</h2>
        <p className="mt-1 text-sm text-foreground-600">来自追更用户的声音</p>
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
          {testimonials.map((t) => (
            <figure key={t.name} className="rounded-lg bg-background-50 p-5">
              <div className="flex items-center gap-1 text-accent-500">
                {[1, 2, 3, 4, 5].map((s) => (
                  <i key={s} className="ri-star-fill text-sm"></i>
                ))}
              </div>
              <blockquote className="mt-3 text-sm leading-relaxed text-foreground-700">“{t.text}”</blockquote>
              <figcaption className="mt-4 flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-100 font-display text-sm text-primary-600">
                  {t.name[0]}
                </span>
                <span>
                  <p className="text-sm font-medium text-foreground-900">{t.name}</p>
                  <p className="text-xs text-secondary-500">{t.role}</p>
                </span>
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* 联系方式 */}
      <section className="mt-12">
        <h2 className="text-xl font-bold text-foreground-950 md:text-2xl">联系我们</h2>
        <p className="mt-1 text-sm text-foreground-600">有建议或反馈，欢迎随时与我们联系</p>
        <div className="mt-6 flex flex-col gap-4 sm:flex-row">
          <a
            href="mailto:support@wujinmanhua.com"
            className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-5 transition-colors hover:border-primary-300"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50">
              <i className="ri-mail-line text-lg"></i>
            </span>
            <span>
              <p className="text-sm font-medium text-foreground-900">邮件联系</p>
              <p className="text-sm text-foreground-600">support@wujinmanhua.com</p>
            </span>
          </a>
          <div className="flex items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-5">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-accent-500 text-background-50">
              <i className="ri-chat-smile-3-line text-lg"></i>
            </span>
            <span>
              <p className="text-sm font-medium text-foreground-900">反馈建议</p>
              <p className="text-sm text-foreground-600">发现漏更或信息有误，欢迎指正</p>
            </span>
          </div>
        </div>
      </section>
    </div>
  );
}